<template>
  <el-dialog :visible="visible" :title="$t('selectStaff.title')" width="80%" @close="handleClose">
    <el-row :gutter="20">
      <el-col :span="12" class="border-right">
        <div class="text-center">
          <h4>{{ $t('selectStaff.orgInfo') }}</h4>
        </div>
        <div class="staff padding">
          <OrgTreeShow ref="orgTree" @org-selected="handleOrgSelected" />
        </div>
      </el-col>
      <el-col :span="12">
        <div class="text-center">
          <h4>{{ $t('selectStaff.staffInfo') }}</h4>
        </div>
        <div class="padding-left staff padding padding-top-xs">
          <div v-if="multi && staffs.length > 0" class="staff-list-header">
            <el-checkbox v-model="selectAll" @change="handleSelectAll">
              {{ $t('selectStaff.selectAll') }}
            </el-checkbox>
            <span class="selected-count" v-if="selectedStaffs.length > 0">
              {{ $t('selectStaff.selectedCount', { count: selectedStaffs.length }) }}
            </span>
          </div>
          <div v-for="(item, index) in staffs" :key="index" class="staff-item"
            :class="{ 'selected': multi ? isSelected(item.userId) : curStaffId == item.userId }"
            @click="handleStaffClick(item)">
            <el-checkbox v-if="multi" :value="isSelected(item.userId)" @click.native.stop @change="toggleStaff(item)"></el-checkbox>
            <div>
              <i class="el-icon-user margin-right-xs"></i>
              {{ item.name || item.userName }}
              <span v-if="item.tel" class="staff-tel">（{{ item.tel }}）</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="handleSubmit">{{ $t('common.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import OrgTreeShow from '@/components/org/OrgTreeShow'
import { getStaffInfos } from '@/api/work/repairTypeUserApi'

export default {
  name: 'SelectStaff',
  components: {
    OrgTreeShow
  },
  props: {
    multi: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      staffs: [],
      curStaffId: '',
      selectedStaff: null,
      selectedStaffs: [],
      selectAll: false,
      staffConfig: {},
      currentOrgId: ''
    }
  },
  methods: {
    open() {
      this.visible = true
      this.$nextTick(() => {
        this.$refs.orgTree.refreshTree()
      })
    },
    handleClose() {
      this.visible = false
      this.staffs = []
      this.curStaffId = ''
      this.selectedStaff = null
      this.selectedStaffs = []
      this.selectAll = false
    },
    handleOrgSelected(org) {
      this.currentOrgId = org.orgId
      this.loadStaff(org.orgId)
    },
    async loadStaff(orgId) {
      try {
        const params = {
          orgId: orgId,
          row: 50,
          page: 1
        }
        const { staffs } = await getStaffInfos(params)

        this.staffs = staffs
        this.selectAll = false
        if (this.multi) {
          this.selectedStaffs = []
        }
        if (this.staffs.length > 0) {
          this.curStaffId = this.staffs[0].userId
          this.selectedStaff = this.staffs[0]
        }
      } catch (error) {
        console.error('Error loading staff:', error)
        this.$message.error(error.message || this.$t('common.requestFailed'))
      }
    },
    isSelected(userId) {
      return this.selectedStaffs.some(s => s.userId === userId)
    },
    toggleStaff(staff) {
      const idx = this.selectedStaffs.findIndex(s => s.userId === staff.userId)
      if (idx > -1) {
        this.selectedStaffs.splice(idx, 1)
      } else {
        this.selectedStaffs.push(staff)
      }
      this.selectAll = this.selectedStaffs.length === this.staffs.length && this.staffs.length > 0
      this.selectedStaff = staff
      this.curStaffId = staff.userId
    },
    handleSelectAll(val) {
      if (val) {
        this.selectedStaffs = [...this.staffs]
      } else {
        this.selectedStaffs = []
      }
    },
    handleStaffClick(item) {
      if (this.multi) {
        this.toggleStaff(item)
      } else {
        this.curStaffId = item.userId
        this.selectedStaff = item
      }
    },
    selectStaff(staff) {
      this.curStaffId = staff.userId
      this.selectedStaff = staff
    },
    handleSubmit() {
      if (this.multi) {
        if (this.selectedStaffs.length > 0) {
          const result = this.selectedStaffs.map(s => ({
            ...s,
            staffId: s.userId,
            staffName: s.userName || s.name,
            staffTel: s.tel
          }))
          this.$emit('selectStaffs', result)
          this.handleClose()
        } else {
          this.$message.warning(this.$t('selectStaff.selectStaffFirst'))
        }
      } else {
        if (this.selectedStaff) {
          this.selectedStaff.staffId = this.selectedStaff.userId
          this.selectedStaff.staffName = this.selectedStaff.userName || this.selectedStaff.name
          this.selectedStaff.staffTel = this.selectedStaff.tel
          this.$emit('selectStaff', this.selectedStaff)
          this.handleClose()
        } else {
          this.$message.warning(this.$t('selectStaff.selectStaffFirst'))
        }
      }
    }
  }
}
</script>

<style scoped>
.border-right {
  border-right: 1px solid #ebeef5;
}

.padding {
  padding: 15px;
}

.staff-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 8px;
}

.selected-count {
  color: #409eff;
  font-size: 12px;
}

.staff-tel {
  color: #909399;
  margin-left: 4px;
}

.staff-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  cursor: pointer;
}

.staff-item:hover {
  background-color: #f5f7fa;
}

.selected {
  background-color: #ecf5ff;
  border: 1px solid #409eff;
}

.text-center {
  text-align: center;
  margin-bottom: 15px;
}
</style>